// A esteira: os casos andam sozinhos e param onde precisam de gente.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import type {
  AneryCard,
  AneryColuna,
  EstadoDaTrilha,
  TravaDoCaso,
  Trilha,
} from '../../../shared/ui/tiposDaEsteira';
import {
  CAPTACAO,
  KIT_OPERADORA,
  LISTA_CAPTACAO,
  MOSTRAR_PROTOTIPO,
  ORDEM_DAS_TRILHAS,
  SUBSTATUS,
  TRILHAS,
} from './dadosDaCaptacao';

/** Colunas, por índice. A última é o fim ruim. */
const NOVO = 0;
/**
 * Avaliação e Documentação eram duas colunas em fila. Viraram UMA, porque o
 * que acontece entre a chegada e o orçamento não é fila: são quatro trilhas
 * com intervenientes próprios correndo ao mesmo tempo. A coluna diz a fase; a
 * tira do card diz qual trilha segura.
 */
const APURACAO = 1;
// O orcamento e o ULTIMO passo da captacao: e ele que a operadora aprova, e
// sem essa aprovacao nao ha implantacao. As tres fontes concordam — Guia 7-8-9,
// Trilha 13-14-15-16, processo canonico 13-16-18.
const ORCAMENTO = 2;
const AUTORIZADO = 3;
const DECLINADO = 4;

/**
 * Os 11 campos de LISTA_CAPTACAO nos três gestos da frente C.
 * Quem liga para a família preenche um grupo por ligação, não um campo.
 */
const GRUPOS_DO_CADASTRO: string[][] = [
  ['telefone', 'telefone_cuidador', 'falou_com'],
  ['endereco', 'bairro_cep_regiao'],
  ['data_nascimento', 'hospital_leito', 'diagnostico', 'liminar', 'ja_assistido', 'pad_anterior'],
];

const VAZIO: Record<Trilha, string> = {
  avaliacao: '',
  documentacao_hospitalar: '',
  cadastro: '',
  validacao_pad: '',
  equipe: '',
};

/** "4d" → 4. Ordena a cobrança sem precisar de relógio na tela. */
function diasDe(idade: string | undefined): number {
  return idade ? Number.parseInt(idade, 10) || 0 : 0;
}

/**
 * A trilha pode andar agora?
 *
 * Só responde "não" quando OUTRA trilha a segura — é a única serialização que
 * sobrou, e ela é real: não se visita sem endereço, não se cota sem NEAD.
 */
function travada(
  t: Trilha,
  feitos: Record<Trilha, number>,
): { porque: string; trilha: Trilha } | null {
  const trava = TRILHAS[t].travas?.[feitos[t]];
  if (!trava) return null;
  return feitos[trava.trilha] >= trava.precisa
    ? null
    : { porque: trava.porque, trilha: trava.trilha };
}

/**
 * `aguardando`: caso REAL da triagem (TP-025). Fica onde entrou até existir um
 * próximo passo de verdade — não anda por timer nem escala, porque o
 * percurso simulado seria inventar o que não aconteceu com um caso que existe.
 */
type Fase = 'andando' | 'parado' | 'fim' | 'aguardando';

interface Estado {
  col: number;
  fase: Fase;
  /**
   * A triagem já terminou neste caso.
   *
   * Vale mesmo para quem entra direto na própria coluna: nenhum caso tem achado
   * no segundo em que chega. Por alguns segundos ele é só "em análise", e só
   * então a tela diz o que falta.
   */
  revelado: boolean;
  /** Passo dentro da coluna — índice na cadeia de SUBSTATUS. Só no orçamento. */
  sub: number;
  /**
   * Passos fechados por trilha, em "Em apuração". Quatro contadores que andam
   * independentes: é o desmembramento que a coluna única escondia.
   */
  feitos: Record<Trilha, number>;
  /** Há quantos "dias" cada trilha está parada. Semente do mock, zera ao andar. */
  paradaHa: Record<Trilha, string>;
  /** Diff da lista 1 (captação) — trava a avaliação. */
  faltaCaptacao: string[];
  /** Diff da lista 2 (kit da operadora) — trava o envio. */
  faltaKit: string[];
  /**
   * Degrau do escalonamento: 0 parado · 1 cobrado · 2 escalado · 3 fora do prazo.
   *
   * O gatilho é TEMPO, nunca opinião da máquina. Nenhum degrau decide nada —
   * o caso só muda de mão e fica mais visível, que é o que a operação faz no
   * papel: cobrança ao núcleo, escalonamento em D+3 ao coordenador, e o SLA da
   * modalidade correndo por cima.
   */
  nivel: number;
}

/**
 * Os degraus do escalonamento, comprimidos para a demo.
 *
 * Na operação são horas e dias — cobrança ao núcleo, D+3 ao coordenador, e o
 * SLA da modalidade (AD 24h · ID 48h · VM 72h) por cima. Aqui são segundos,
 * para o caso apodrecer na frente de quem está assistindo.
 */
const DEGRAUS = [
  { apos: 15000, rotulo: 'cobrança enviada ao núcleo', dono: null, toast: 'Cobrança enviada ao núcleo, com trilha.' },
  { apos: 15000, rotulo: 'escalado ao coordenador', dono: 'Coordenador do Núcleo', toast: 'Sem resposta do núcleo. Escalado ao coordenador.' },
  { apos: 15000, rotulo: 'fora do prazo — gerência avisada', dono: 'Gerência', toast: 'Prazo estourado. Gerência avisada.' },
];

/**
 * Quanto uma pessoa demora para resolver o que só ela pode resolver.
 *
 * Mais longo que um passo automático de propósito: na tela dá para VER que o
 * caso ficou esperando gente, e é isso que a fila de trabalho existe para
 * mostrar. Decisão de alçada demora mais que preencher um endereço, como na
 * vida real.
 */

/** Casos que chegam durante a apresentação, como se o SIAI tivesse disparado. */
/**
 * O que cada SUBSTATUS pede, na ordem dos substatus da coluna.
 *
 * O rótulo do botão era por coluna, e os seis passos da avaliação diziam todos
 * "acionar a avaliadora" — inclusive o passo em que a avaliadora já tinha ido.
 * Cada linha aqui é um gesto de uma pessoa, e o botão tem que nomear o gesto.
 */
const PASSOS: Record<string, { botao: string; feito: string; acao: string; responsavel: string }[]> = {
  // O leitor extrai o SIAI sozinho — isso é máquina. ABRIR o caso não é: é
  // conferir que o paciente é aquele mesmo, e homônimo aberto errado é como
  // nasce duplicata no IW. Por isso o quadro para aqui e espera o clique.
  'Novo (SIAI)': [
    {
      botao: 'Abrir o caso',
      feito: 'Caso aberto.',
      acao: 'O anexo ainda está sendo lido.',
      responsavel: 'Enf. de Captação',
    },
    {
      botao: 'Abrir o caso e iniciar a apuração',
      feito: 'Caso aberto. As cinco frentes começam.',
      acao: 'Conferir o que o leitor extraiu do SIAI e abrir o caso no paciente certo.',
      responsavel: 'Enf. de Captação',
    },
  ],
  'Orçamento': [
    {
      botao: 'Calcular o orçamento no IW',
      feito: 'Orçamento calculado.',
      acao: 'Lançar prescrição, medicação, kits, equipamentos, O₂, equipe e visitas — e rodar o cálculo.',
      responsavel: 'Enf. de Captação',
    },
    {
      botao: 'Consolidar cooperativa e escala',
      feito: 'Valores consolidados.',
      acao: 'Juntar o que a cooperativa ofertou com o que a escala interna cobre.',
      responsavel: 'Enf. de Captação',
    },
    {
      botao: 'Rodar a análise de rentabilidade',
      feito: 'Rentabilidade analisada.',
      acao: 'O relatório financeiro do IW diz a margem. É ela que define a alçada.',
      responsavel: 'Coordenação do Núcleo',
    },
    {
      botao: 'Aprovar a margem',
      feito: 'Margem aprovada.',
      acao: 'A margem está fora da faixa que o Coordenador aprova sozinho. Precisa de quem tem alçada.',
      responsavel: 'Gerência Operacional',
    },
    {
      botao: 'Enviar à operadora',
      feito: 'Orçamento enviado. Aguardando o retorno da operadora.',
      acao: 'Enviar avaliação, NEAD e orçamento. É o retorno dela que autoriza a implantação.',
      responsavel: 'Coordenação do Núcleo',
    },
  ],
};

const CHEGANDO: AneryCard[] = [
  {
    titulo: 'Pac. 1212',
    operadora: 'Bradesco',
    sub: 'AD · desospitalização amanhã',
    meta: 'chegou agora · SLA 24h',
    detalhe: {
      modalidade: 'AD · atendimento domiciliar',
      canal: 'portal SIAI → caixa bradescosp@anery.com.br',
      solicitacao: 'solicitação 4471944 · cartão ****2071',
      dono: { nome: 'Ana', papel: 'Enf. de Captação', nucleo: 'SP-1' },
      momento: {
        tipo: 'preencher',
        texto: 'Chegou agora. O cadastro veio com cartão, nome e PAD.',
        acao: 'Completar a captação e acionar a avaliadora.',
        responsavel: 'Enf. de Captação',
      },
      entrada: {
        campos: ['endereco', 'telefone_cuidador', 'diagnostico', 'liminar'],
        botao: 'Salvar e acionar a avaliadora',
        feito: 'Captação preenchida e avaliadora acionada.',
      },
    },
  },
];

function useMovimentoReduzido() {
  const [reduzido] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  return reduzido;
}

export function useCaptacaoSimulacao() {
  const base = CAPTACAO.colunas;
  const reduzido = useMovimentoReduzido();

  /**
   * Todos os cards, achatados. A coluna em que o mock os declara nao importa
   * mais: todos entram por "Novo (SIAI)" e quem decide o desfecho e a operacao.
   */
  const cards = useMemo(() => base.flatMap((col) => col.cards), [base]);

  const [extras, setExtras] = useState<AneryCard[]>([]);


  /**
   * O quadro começa VAZIO.
   *
   * Todo caso entra por notificação, em "Novo (SIAI)", e só depois anda. É o
   * que acontece na operação — e resolve uma incoerência: um caso não pode
   * exibir "barrado por falta de TCLE" na chegada, porque o TCLE só é exigido
   * lá na documentação. Enquanto o caso não alcança a coluna onde o achado
   * existe, ele aparece como o que é: em análise.
   */
  const [estado, setEstado] = useState<Record<string, Estado>>({});

  const [chegou, setChegou] = useState<Set<string>>(new Set());
  /** Motivo do declínio, pelo código padronizado — fica na trilha do caso. */
  const [motivos, setMotivos] = useState<Record<string, string>>({});
  const timers = useRef<number[]>([]);

  const marcarChegada = useCallback((titulo: string) => {
    setChegou((s) => new Set(s).add(titulo));
    const id = window.setTimeout(() => {
      setChegou((s) => {
        const n = new Set(s);
        n.delete(titulo);
        return n;
      });
    }, 1400);
    timers.current.push(id);
  }, []);

  /**
   * Nada mais anda sozinho.
   *
   * O caso chega, o leitor extrai o SIAI, e o quadro PARA. Antes, a esteira
   * empurrava o caso de "Novo" para a apuração por um timer — e isso é a tela
   * afirmando que a máquina abriu o caso, quando abrir caso é conferir
   * identidade de paciente. O único trecho automático que sobra é a leitura
   * do anexo, que termina em `revelado` e estaciona o caso.
   */

  /** Carimbos que o escalonamento acrescenta à trilha do caso. */
  const [carimbosExtra, setCarimbosExtra] = useState<Record<string, { rotulo: string; quando: string }[]>>({});
  const escalonando = useRef<Set<string>>(new Set());

  // Caso parado apodrece sozinho: cobra, escala, estoura. Nenhum degrau decide.
  useEffect(() => {
    if (reduzido) return;
    Object.entries(estado).forEach(([titulo, e]) => {
      const chave = `${titulo}:${e.nivel}`;
      if (e.fase !== 'parado' || e.nivel >= DEGRAUS.length || escalonando.current.has(chave)) return;
      const degrau = DEGRAUS[e.nivel];
      if (!degrau) return;
      escalonando.current.add(chave);
      const id = window.setTimeout(() => {
        setEstado((p) =>
          p[titulo]?.fase === 'parado' ? { ...p, [titulo]: { ...p[titulo], nivel: p[titulo].nivel + 1 } } : p,
        );
        setCarimbosExtra((c) => ({
          ...c,
          [titulo]: [...(c[titulo] ?? []), { rotulo: degrau.rotulo, quando: 'agora' }],
        }));
        toast.warning(titulo, { description: degrau.toast });
      }, degrau.apos);
      timers.current.push(id);
    });
  }, [estado, reduzido]);

  /**
   * Títulos que já chegaram. Checado ANTES de qualquer `setState`: reprocessar
   * a chegada de um caso que já anda devolveria ele a "Novo".
   */
  const recebidos = useRef<Set<string>>(new Set());

  /** Uma notificação: coloca UM caso em "Novo (SIAI)". */
  const receberCaso = useCallback(
    (card: AneryCard, fase: Fase = 'andando') => {
      if (recebidos.current.has(card.titulo)) return;
      recebidos.current.add(card.titulo);

      setExtras((p) =>
        cards.some((c) => c.titulo === card.titulo) || p.some((c) => c.titulo === card.titulo)
          ? p
          : [...p, card],
      );
      setEstado((p) => ({
        ...p,
        [card.titulo]: {
          // TODO caso entra por "Novo". Ninguém nasce autorizado nem
          // declinado: o desfecho é da operação, não do mock.
          col: NOVO,
          // O fabricado anda até terminar a leitura do anexo; o real espera o
          // próximo passo (`aguardando`).
          fase,
          // Nunca antes de "Em análise": em "Novo" o caso só chegou, e
          // caso que chegou não tem achado nem dono de pendência. Quem foi
          // declarado no mock já em Novo espera a triagem da coluna 1.
          revelado: false,
          sub: 0,
          // As trilhas começam onde o caso já está: documentos conta o
          // que o kit já tem, cadastro conta os grupos completos. Nenhum
          // dos dois é chute — sai da mesma varredura de sempre.
          feitos: {
            avaliacao: 0,
            documentacao_hospitalar: 0,
            cadastro: GRUPOS_DO_CADASTRO.filter((g) =>
              g.every((campo) => card.detalhe?.tem?.includes(campo) ?? false),
            ).length,
            validacao_pad: 0,
            equipe: 0,
          },
          paradaHa: { ...VAZIO, ...(card.detalhe?.paradaHa ?? {}) },
          // As duas varreduras: lista menos o que o caso já tem. Ausente
          // em `tem`/`temDocs` quer dizer completo — nenhum card escreve a
          // própria pendência.
          faltaCaptacao: LISTA_CAPTACAO.filter(
            (k) => card.detalhe?.tem !== undefined && !card.detalhe.tem.includes(k),
          ),
          nivel: 0,
          faltaKit: (KIT_OPERADORA[card.operadora ?? ''] ?? []).filter(
            (k) => card.detalhe?.temDocs !== undefined && !card.detalhe.temDocs.includes(k),
          ),
        },
      }));
      // Triagem: 3 a 7 segundos até o caso ter um achado.
      const idRev = window.setTimeout(
        () => { setEstado((q) =>
            (() => {
              const atual = q[card.titulo];
              if (!atual) return q;
              // O real já foi lido pela triagem: não estaciona em `parado`,
              // porque o escalonamento cobraria um passo que ainda não existe.
              if (atual.fase === 'aguardando') {
                return { ...q, [card.titulo]: { ...atual, revelado: true } };
              }
              // Terminou de ler o anexo: o caso fica PARADO em "Novo",
              // esperando alguém abri-lo. O relógio de escalonamento
              // começa aqui, e é o que deve começar — SIAI parado na
              // caixa é exatamente o que a fila existe para mostrar.
              return { ...q, [card.titulo]: { ...atual, revelado: true, sub: 1, fase: 'parado' } };
            })(),
          ); },
        3000 + Math.random() * 4000,
      );
      timers.current.push(idRev);
      marcarChegada(card.titulo);
      // A notificação de chegada diz o que a operadora mandou — modalidade
      // e número da solicitação. O `sub` do card carrega o achado (TCLE
      // pendente, escala vencida), e achado não existe na chegada.
      toast([card.titulo, card.operadora].filter(Boolean).join(' · '), {
        description: [card.detalhe?.modalidade, card.detalhe?.solicitacao]
          .filter(Boolean)
          .join(' · '),
      });
    },
    [cards, marcarChegada],
  );

  /** Casos que vieram da triagem de verdade (TP-025) — a página os marca. */
  const [reais, setReais] = useState<Set<string>>(new Set());

  /**
   * `TP-028`: casos que entraram pelo portão sem certeza (fail-open, D3) — a
   * página marca com uma segunda `Tag`, ao lado de "e-mail real".
   */
  const [incertos, setIncertos] = useState<Set<string>>(new Set());

  /**
   * Chegada de um caso real da triagem. Entra por "Novo" como qualquer
   * notificação e FICA lá (`aguardando`) até existir o próximo passo — a
   * esteira não inventa percurso para um caso que existe.
   *
   * Chega mesmo com movimento reduzido, ao contrário das fixtures: esconder
   * um caso que existe é pior do que mostrá-lo parado.
   */
  const receberCasoReal = useCallback(
    (card: AneryCard) => {
      if (recebidos.current.has(card.titulo)) return;
      setReais((s) => new Set(s).add(card.titulo));
      if (card.classificacaoIncerta) setIncertos((s) => new Set(s).add(card.titulo));
      receberCaso(card, 'aguardando');
    },
    [receberCaso],
  );

  // Cada notificação coloca UM caso em "Novo (SIAI)". Nada nasce no quadro.
  useEffect(() => {
    // Com o protótipo desligado, nenhum cartão fabricado é semeado — só o que
    // `receberCasoReal` traz da triagem. Ver `MOSTRAR_PROTOTIPO`.
    if (!MOSTRAR_PROTOTIPO) return;
    if (reduzido) return;
    const fila = [...cards, ...CHEGANDO];
    const ids = fila.map((card, i) =>
      window.setTimeout(() => { receberCaso(card); }, 800 + i * (1100 + Math.random() * 1200)),
    );
    return () => { ids.forEach(clearTimeout); };
  }, [reduzido, receberCaso, cards]);

  useEffect(() => () => { timers.current.forEach(clearTimeout); }, []);

  /**
   * A pessoa agiu no detalhe do caso.
   *
   * Na avaliação, preencher o que faltava só destrava: o caso volta a andar e
   * segue a esteira. Na documentação, a ação É o desfecho — autorizado ou
   * declinado, e declinar exige o motivo padronizado do Anexo 4.
   */
  const resolver = useCallback(
    (titulo: string, desfecho: 'autorizar' | 'declinar', motivo?: string, trilha?: Trilha) => {
      setEstado((p) => {
        const e = p[titulo];
        if (!e) return p;

        // Declinar é desfecho em qualquer ponto — e na operação é mesmo: dos 11
        // motivos de cancelamento, nove matam o caso antes do orçamento.
        if (desfecho === 'declinar') {
          return { ...p, [titulo]: { ...e, col: DECLINADO, sub: 0, fase: 'fim' } };
        }

        // Em "Novo" o gesto é um só: abrir o caso. Fecha a cadeia curta da
        // coluna e o caso entra na apuração, com as cinco frentes zeradas.
        if (e.col === NOVO) {
          const cadeiaNovo = SUBSTATUS[base[NOVO]?.nome ?? ''] ?? [];
          const seguinte = e.sub + 1;
          if (seguinte < cadeiaNovo.length) {
            return { ...p, [titulo]: { ...e, sub: seguinte, fase: 'parado', nivel: 0 } };
          }
          return { ...p, [titulo]: { ...e, col: APURACAO, sub: 0, fase: 'parado', nivel: 0 } };
        }

        // Em apuração o gesto fecha UM passo de UMA trilha — as outras três
        // não se mexem. É a diferença inteira em relação à fila de antes, onde
        // qualquer clique empurrava o caso para a frente.
        if (e.col === APURACAO) {
          const t = trilha ?? 'cadastro';
          const feitos = { ...e.feitos, [t]: Math.min(e.feitos[t] + 1, TRILHAS[t].passos.length) };
          // A trilha que andou zera o relógio; as outras seguem envelhecendo.
          const paradaHa = { ...e.paradaHa, [t]: '' };
          const limpo = {
            ...e,
            feitos,
            paradaHa,
            // Só some a pendência da trilha que fechou o passo.
            faltaCaptacao: t === 'cadastro' ? [] : e.faltaCaptacao,
            // Cada gesto recomeça o escalonamento: alguém pegou o caso.
            nivel: 0,
          };
          const todasFechadas = ORDEM_DAS_TRILHAS.every((x) => feitos[x] >= TRILHAS[x].passos.length);
          // A convergência deixa de ser combinado e vira regra: o caso só
          // alcança o orçamento com as quatro fechadas.
          if (!todasFechadas) return { ...p, [titulo]: { ...limpo, fase: 'parado' } };
          toast.success(`${titulo} · apuração concluída`, {
            description: 'As cinco frentes fecharam. O caso segue para a margem.',
          });
          return { ...p, [titulo]: { ...limpo, col: ORCAMENTO, sub: 0, fase: 'parado' } };
        }

        // No orçamento a cadeia é cadeia mesmo: calcular, consolidar, medir a
        // rentabilidade, passar pela alçada, enviar. Um depende do anterior.
        const cadeia = SUBSTATUS[base[e.col]?.nome ?? ''] ?? [];
        const proximo = e.sub + 1;
        const limpo = { ...e, faltaCaptacao: [], faltaKit: [] };

        if (proximo < cadeia.length) {
          return { ...p, [titulo]: { ...limpo, sub: proximo, fase: 'parado' } };
        }
        return { ...p, [titulo]: { ...limpo, col: AUTORIZADO, sub: 0, fase: 'fim' } };
      });
      if (motivo) setMotivos((m) => ({ ...m, [titulo]: motivo }));
      marcarChegada(titulo);
    },
    [base, marcarChegada],
  );

  /**
   * Como o caso aparece antes de ter achado.
   *
   * Em "Novo" ele só chegou; e enquanto a triagem não termina, não há pendência
   * nenhuma para mostrar. Exibir achado antes disso seria inventar uma
   * descoberta que não aconteceu.
   */
  const emTransito = (card: AneryCard, recemChegado = false): AneryCard => ({
    titulo: card.titulo,
    operadora: card.operadora,
    sub: card.detalhe?.modalidade ?? card.sub,
    meta: recemChegado ? 'Recebido' : 'Em avaliação',
    detalhe: card.detalhe && {
      ...card.detalhe,
      // O orçamento é PRODUZIDO na etapa de orçamento. Antes dela o número não
      // existe — nem para nós, nem para a operação. Mostrar a margem antes de
      // alguém calcular é a tela afirmando o que não sabe.
      orcamento: undefined,
      entrada: undefined,
      momento: recemChegado
        ? {
            tipo: 'recebido',
            texto: 'O caso acabou de entrar. Ninguém olhou ainda.',
            acao: 'Nada a fazer: a triagem começa sozinha.',
          }
        : {
            tipo: 'analise',
            texto: 'Em avaliação. Nenhuma pendência levantada até aqui.',
            acao: 'Nada a fazer enquanto a etapa corre.',
          },
    },
  });

  /**
   * Todo caso parado oferece as duas saídas.
   *
   * O que ele pede de específico (anexar o TCLE, escolher a cooperativa) vem do
   * próprio caso; o caminho de declínio é sempre possível, porque na operação
   * ele sempre é — e sem isso metade do fluxo não se demonstra.
   */
  const comDecisao = (card: AneryCard, e: Estado): AneryCard => {
    const orc = card.detalhe?.orcamento;

    // Em apuração o caso não tem UM estado, tem quatro. A tira mostra as
    // quatro; a decisão de qual delas cobrar é derivada, nunca digitada.
    const trilhas = Object.fromEntries(
      ORDEM_DAS_TRILHAS.map((t) => {
        const total = TRILHAS[t].passos.length;
        const feitos = Math.min(e.feitos[t], total);
        const espera = feitos < total ? travada(t, e.feitos) : null;
        return [
          t,
          {
            feitos,
            total,
            situacao: feitos >= total ? '' : (espera?.porque ?? TRILHAS[t].passos[feitos]?.rotulo ?? ''),
            idade: feitos >= total || espera ? undefined : e.paradaHa[t],
            esperando: Boolean(espera),
            ...(espera ? { esperaDe: espera.trilha } : {}),
            ...(feitos < total && !espera
              ? {
                  botao: TRILHAS[t].passos[feitos]?.botao ?? '',
                  responsavel: TRILHAS[t].passos[feitos]?.responsavel ?? '',
                  campos: TRILHAS[t].passos[feitos]?.campos ?? [],
                  acao: TRILHAS[t].passos[feitos]?.acao ?? '',
                  gesto: TRILHAS[t].passos[feitos]?.gesto ?? 'confirma',
                }
              : {}),
            tom: feitos >= total ? 'ok' : espera ? 'neutral' : diasDe(e.paradaHa[t]) >= 3 ? 'block' : 'warn',
          } satisfies EstadoDaTrilha,
        ];
      }),
    ) as Record<Trilha, EstadoDaTrilha>;

    // Quem segura o caso: a trilha que PODE andar e está parada há mais tempo.
    // Trilha esperando outra não é cobrável — cobrar quem não pode agir é o
    // que a fila de hoje faz, e é por isso que ninguém confia nela.
    const cobraveis = ORDEM_DAS_TRILHAS.filter(
      (t) => trilhas[t].feitos < trilhas[t].total && !trilhas[t].esperando,
    ).sort((a, b) => diasDe(e.paradaHa[b]) - diasDe(e.paradaHa[a]));
    const alvo = cobraveis[0];
    const gestoDaTrilha = alvo ? TRILHAS[alvo].passos[e.feitos[alvo]] : undefined;
    const trava: TravaDoCaso | undefined =
      alvo && gestoDaTrilha
        ? {
            trilha: alvo,
            motivo: `${gestoDaTrilha.rotulo}${e.paradaHa[alvo] ? ` · há ${e.paradaHa[alvo]}` : ''}`,
            responsavel: gestoDaTrilha.responsavel,
          }
        : undefined;
    const fechadas = ORDEM_DAS_TRILHAS.filter((t) => trilhas[t].feitos >= trilhas[t].total).length;

    // Cada coluna pede uma coisa diferente, e o formulário tem que dizer qual.
    // Antes isto era um booleano `naAvaliacao`, e tudo que não fosse avaliação
    // virava documentação — com a coluna de orçamento, o card passou a pedir
    // "enviar o processo" numa etapa em que o processo já foi enviado.
    const nomeCol = base[e.col]?.nome ?? '';
    const gesto = PASSOS[nomeCol]?.[e.sub];

    // A coluna decide O QUE se vê; em apuração é a TRILHA que decide o que se faz.
    const passo =
      e.col === NOVO
        ? {
            campos: [] as string[],
            face: {
              sub: card.detalhe?.modalidade ?? card.sub,
              meta: 'Lido — aguardando abertura',
            },
            texto:
              'O leitor terminou de extrair o SIAI. Abrir o caso é conferir de quem ele é — e é isso que a máquina não faz sozinha.',
            tipo: 'revisao' as const,
          }
        : e.col === APURACAO
        ? {
            campos: alvo === 'cadastro' ? e.faltaCaptacao : [],
            face: {
              sub: card.detalhe?.modalidade ?? card.sub,
              meta: `${fechadas} de ${ORDEM_DAS_TRILHAS.length} frentes fechadas`,
            },
            texto: alvo
              ? `${ORDEM_DAS_TRILHAS.length - fechadas} frentes abertas. A que segura o caso é ${TRILHAS[alvo].rotulo}: ${gestoDaTrilha?.rotulo ?? ''}.`
              : 'As cinco frentes fecharam. O caso está pronto para a margem.',
            tipo: alvo === 'cadastro' ? ('preencher' as const) : ('handoff' as const),
          }
        : {
              // No orçamento não falta dado: o que existe é uma DECISÃO de
              // margem. Por isso `campos` é vazio — o formulário some e sobra
              // o que o caso consome e quanto sobra.
              campos: [] as string[],
              face: {
                sub: orc ? `${orc.recursos.length} itens · equipe de ${orc.equipe.length}` : 'Montando o orçamento',
                meta: orc ? `Margem ${orc.margem.percentual}%` : 'Sem orçamento calculado',
              },
              texto: orc
                ? `Orçamento fechado em ${orc.margem.percentual}% de margem, com ${orc.recursos.length} itens e ${orc.equipe.length} categorias de equipe. ${orc.margem.alcada}.`
                : 'Não há o que orçar: sem paciente identificado não há prescrição, nem equipe, nem itens. O caso chegou até aqui sem passar pela identificação.',
          tipo: orc && orc.margem.tone !== 'ok' ? ('alcada' as const) : ('envio' as const),
        };

    // Em apuração o gesto vem da trilha cobrada; no orçamento, da cadeia.
    const acionavel = e.col === APURACAO ? gestoDaTrilha : gesto;

    return {
      ...card,
      sub: passo.face.sub,
      meta: passo.face.meta,
      ...(e.col === APURACAO ? { trilhas, ...(trava ? { trava } : {}) } : {}),
      detalhe: card.detalhe && {
        ...card.detalhe,
        // Idem: antes da etapa de orçamento o custo ainda não foi levantado.
        orcamento: e.col >= ORCAMENTO ? card.detalhe.orcamento : undefined,
        entrada: {
          campos: passo.campos,
          botao: acionavel?.botao ?? 'Registrar e seguir',
          botaoAlt: 'Declinar o caso',
          feito: acionavel?.feito ?? 'Registrado.',
        },
        carimbos: [...(card.detalhe.carimbos ?? []), ...(carimbosExtra[card.titulo] ?? [])],
        momento: {
          tipo: e.nivel >= 3 ? 'atrasado' : e.nivel >= 2 ? 'escalado' : passo.tipo,
          texto: passo.texto,
          acao:
            e.nivel >= 3
              ? 'Fora do prazo. A gerência já foi avisada e o caso segue sem resposta.'
              : e.nivel >= 2
                ? 'Sem resposta do núcleo. O coordenador assumiu a cobrança.'
                : (acionavel?.acao ?? ''),
          // O dono muda com o degrau: é o escalonamento acontecendo na tela.
          responsavel:
            DEGRAUS.slice(0, e.nivel).reverse().find((d) => d.dono)?.dono ??
            acionavel?.responsavel ??
            'Enf. de Captação',
        },
      },
    };
  };

  /**
   * No fim da fila o que importa é o desfecho.
   *
   * Um caso implantado não pode continuar anunciando "falta informação": o que
   * faltava foi resolvido para ele chegar até aqui. O estado do meio do caminho
   * é substituído pelo desfecho, e o formulário sai — não há mais o que
   * destravar.
   */
  const noFim = (card: AneryCard, col: number): AneryCard => {
    if (col !== AUTORIZADO && col !== DECLINADO) return card;
    const implantado = col === AUTORIZADO;
    return {
      ...card,
      meta: implantado ? 'Implantado' : 'Declinado',
      detalhe: card.detalhe && {
        ...card.detalhe,
        entrada: undefined,
        momento: {
          tipo: implantado ? 'implantado' : 'declinado',
          texto: implantado
            ? 'Caso autorizado. O que faltava foi resolvido no caminho.'
            : motivos[card.titulo]
              ? `Declinado no IW · ${motivos[card.titulo]}`
              : card.detalhe.momento?.texto ?? 'Caso declinado.',
          acao: implantado
            ? 'Acompanhamento passa para a case de gerenciamento.'
            : 'Declínio devolvido à operadora, com justificativa.',
        },
      },
    };
  };

  const colunas: AneryColuna[] = useMemo(() => {
    const todos = [...cards, ...extras];
    return base.map((col, i) => {
      const dentro = todos
        .flatMap((c) => {
          const e = estado[c.titulo];
          if (e?.col !== i) return [];
          const cadeia = SUBSTATUS[col.nome] ?? [];
          const substatus = cadeia[e.sub];
          // Caso real aguardando: mostrado como a triagem o leu, sem substatus
          // simulado nem o "a triagem começa sozinha" — para ele a triagem já
          // terminou.
          if (e.fase === 'aguardando') return [c];
          // "Novo" nunca mostra achado: o caso chegou, e é só isso que se sabe.
          // Três estados possíveis, e nada mais: terminou, está parado por uma
          // varredura, ou está andando. Andando não tem achado — o achado é
          // resultado da varredura, e a varredura roda na virada de fase.
          const base =
            e.fase === 'fim'
              ? noFim(c, e.col)
              : e.fase === 'parado'
                ? comDecisao(c, e)
                : emTransito(c, e.col === NOVO);
          return { ...base, substatus };
        });
      return { ...col, contagem: String(dentro.length), cards: dentro };
    });
    // `comDecisao`/`noFim` são puras e estáveis; entram fora da lista de
    // propósito para a esteira não remontar a cada tique.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, cards, extras, estado, motivos, carimbosExtra]);

  const vals = Object.values(estado);
  const parados = vals.filter((e) => e.fase === 'parado').length;
  const cobrados = vals.filter((e) => e.fase === 'parado' && e.nivel >= 1).length;
  const foraDoPrazo = vals.filter((e) => e.fase === 'parado' && e.nivel >= 3).length;
  /** Casos escalados piscam: mudaram de mão e ninguém respondeu. */
  const piscando = new Set(
    Object.entries(estado)
      .filter(([, e]) => e.fase === 'parado' && e.nivel >= 2)
      .map(([t]) => t),
  );

  return {
    colunas,
    chegou,
    resolver,
    parados,
    cobrados,
    foraDoPrazo,
    piscando,
    ativo: !reduzido,
    receberCasoReal,
    reais,
    incertos,
  };
}
